import FJConfig from '../../config/FJConfig';
import FJNetworkStore from '../../store/FJNetworkStore';
import FJUtil from '../../util/FJUtil';
import { decode, toBbox } from '../../util/sm2/mask';
import FJVideoTrackSocket from '../FJWebSocket/FJVideoTrackSoket';

class FJTracker {
    _tracklets = [];

    // websocket 连接 id
    _websocket = null;
    _connectId = null;

    // 中止控制器
    _abortController = null;

    constructor() {
        this._tracklets = [];

        this._connectId = Date.now();
        this._websocket = new FJVideoTrackSocket({
            url: FJConfig.webSocketUrl,
            connectId: this._connectId,
        });
    }

    /**
     * @desc 创建轨迹
     * @param {Object} point
     */
    createTracklet() {
        // 这将为空的跟踪片段返回 0，否则返回下一个最大数字。
        const nextId = Object.values(this._tracklets).reduce((prev, curr) => Math.max(prev, curr.id), -1) + 1;

        const newTracklet = {
            id: nextId,
            color: '#0000ff',
            thumbnail: null,
            points: [],
            masks: [],
            isInitialized: false,
            currentTime: 0,
        };

        this._tracklets[nextId] = newTracklet;
    }

    async updatePoints(params, callback, failCallBack) {
        const { currentTime, objectId, points, base64String } = params;
        const tracklet = this._tracklets[objectId];
        if (!tracklet) {
            return Promise.reject('tracklet for object id %s not initialized');
        }

        const postPointsData = points.filter(point => point[2] === 1);

        try {
            const data = await FJNetworkStore.videoRemoveBackgroundAddPoints(
                currentTime,
                objectId,
                postPointsData,
                base64String,
            );

            if (data && data.code === 214) {
                throw new Error('not login');
            }

            if (tracklet.currentTime !== currentTime) {
                tracklet.currentTime = currentTime;
                tracklet.points = [];
                tracklet.masks = [];
            }

            if (!data) {
                throw new Error('Failed to add points');
            }

            tracklet.isInitialized = true;
            tracklet.points = points;
            await this._updateTrackletMasks(data.data);
            callback();
        } catch (error) {
            failCallBack(error);
        }
    }

    async websocketConnect() {
        await this._websocket.connect();
    }

    /**
     * @desc 绑定事件
     * @param {String} eventName
     * @param {Function} trackCallback
     */
    websocketBindEvents(eventName, trackCallback) {
        if (!this._websocket) {
            return;
        }
        this._websocket.bindEvents(eventName, trackCallback);
    }

    /**
     * @desc 解绑事件
     * @param {String} eventName
     */
    websocketUnbindEvents(eventName) {
        if (!this._websocket) {
            return;
        }
        this._websocket.unbindEvents(eventName);
    }

    /**
     * @desc 获取websocket id
     * @returns {String}
     */
    getWebsocketId() {
        return this._connectId;
    }

    /**
     * @desc 开始跟踪视频
     */
    async startTrack(videoPath, maskPath, duration, websocketCallback) {
        try {
            await this.websocketConnect();

            this.websocketBindEvents('track', websocketCallback);

            const tracklet = this._tracklets[0];

            const labelList = [];
            const pointList = [];

            tracklet.points.forEach((point, index) => {
                if (point[2] === 1) {
                    labelList.push(index + 1);
                    pointList.push([point[0], point[1]]);
                }
            });

            const startFrameIndex = Math.round(tracklet.currentTime * 30);
            const points = [
                {
                    frame_index: startFrameIndex,
                    clear_old_points: true,
                    object_id: tracklet.id,
                    labels: labelList,
                    points: pointList,
                },
            ];

            const options = {
                url: videoPath,
                points,
                start_frame_index: startFrameIndex,
                cid: this._connectId,
                duration,
                mask_url: maskPath,
            };

            return await FJNetworkStore.videoRemoveBackgroundTrack(options);
        } catch (error) {
            console.error('Failed to clear tracklet masks', error);
        }
    }

    /**
     * @desc 清除所有跟踪片段
     */
    clearTracklets() {
        this._tracklets = [];
    }

    /**
     * @desc 获取轨迹
     * @returns {Array}
     */
    getTracklets() {
        return FJUtil.deepClone(this._tracklets);
    }

    /**
     * @desc 删除点
     * @param {Number} trackletId
     */
    deleteAllPoints(trackletId) {
        this._tracklets[trackletId] = {
            id: trackletId,
            color: '#0000ff',
            thumbnail: null,
            points: [],
            masks: [],
            isInitialized: false,
            currentTime: 0,
        };
    }

    /**
     * @desc 删除点
     * @param {Number} trackletId
     * @param {Number} index
     */
    deletePoints(trackletId, index) {
        const tracklet = this._tracklets[trackletId];
        if (!tracklet) {
            return;
        }
        tracklet.points.splice(index, 1);
    }

    /**
     * @desc 更新轨迹的掩码
     * @param {Object} data
     */
    async _updateTrackletMasks(data) {
        const { rle_mask_list: rleMaskList } = data;

        for (const { object_id: objectId, rle_mask: rleMask } of rleMaskList) {
            const track = this._tracklets[objectId];
            const { size, counts } = rleMask;

            const rleObject = {
                size: [size[0], size[1]],
                counts: counts,
            };

            const decodedMask = decode([rleObject]);
            const bbox = toBbox([rleObject]);

            const mask = {
                data: rleObject,
                shape: [...decodedMask.shape],
                bounds: [
                    [bbox[0], bbox[1]],
                    [bbox[0] + bbox[2], bbox[1] + bbox[3]],
                ],
                isEmpty: false,
            };

            track.masks = [mask];
        }
    }

    /**
     * @desc 清除所有轨迹的掩码
     */
    _clearTrackletMasks() {
        const keys = Object.keys(this._tracklets);
        for (const key of keys) {
            const trackletId = Number(key);
            const tracklet = {
                ...this._tracklets[trackletId],
                masks: [],
            };
            this._tracklets[trackletId] = tracklet;
        }
    }
}

export default FJTracker;
